// Port of webapp/src/components/newBoard.tsx + the template picker in
// components/boardTemplateSelector — reduced to: name, icon, description and a
// template to start from.

import {FormEvent, ReactElement, useEffect, useState} from 'react'
import classnames from 'classnames'
import {useNavigate} from 'react-router-dom'
import {Board} from '../../types/board'
import {buildBoardPath} from '../../common/paths'
import * as mutator from '../../state/mutator'
import {useTeams} from '../../store/teams'
import {useBoards} from '../../store/boards'
import {useViews} from '../../store/views'
import client from '../../api/client'
import {Dialog} from '../common/dialog'
import {Icon, viewIcons} from '../common/icons'

const icons = ['📋', '🚀', '🐞', '🗓️', '✅', '🎯', '💡', '🧪', '📊', '🏁']

export default function NewBoardDialog({onClose}: {onClose: () => void}): ReactElement {
    const navigate = useNavigate()
    const teamId = useTeams((state) => state.activeTeamId)
    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const [icon, setIcon] = useState(icons[0])
    const [templates, setTemplates] = useState<Board[]>([])
    const [templateId, setTemplateId] = useState('tpl-kanban')

    useEffect(() => {
        client.getTemplates(teamId).then(setTemplates)
    }, [teamId])

    const template = templates.find((t) => t.id === templateId)

    const submit = async (event: FormEvent): Promise<void> => {
        event.preventDefault()
        const created = await mutator.createBoardWithViews(teamId, {
            title: title.trim() || '未命名',
            description,
            icon,
            fromTemplate: template,
        })
        await Promise.all([
            useBoards.getState().loadBoard(created.id),
            useViews.getState().loadViews(created.id),
        ])
        onClose()
        navigate(buildBoardPath({teamId, boardId: created.id}))
    }

    return (
        <Dialog title='新建看板' onClose={onClose}>
            <form onSubmit={submit} className='space-y-4'>
                <div>
                    <label className='mb-1 block text-xs font-semibold uppercase opacity-60' htmlFor='board-title'>标题</label>
                    <div className='flex items-center gap-2'>
                        <input
                            id='board-title'
                            autoFocus={true}
                            className='w-full rounded border border-main-fg/20 bg-transparent px-2 py-1.5 outline-none focus:border-button-bg'
                            value={title}
                            placeholder='未命名'
                            onChange={(event) => setTitle(event.target.value)}
                        />
                        <button
                            type='button'
                            className='rounded border border-main-fg/20 px-2 py-1.5 text-lg leading-none'
                            onClick={() => setIcon(icons[(icons.indexOf(icon) + 1) % icons.length])}
                            aria-label='选择图标'
                            title={icon}
                        >
                            {icon}
                        </button>
                    </div>
                </div>

                <div>
                    <label className='mb-1 block text-xs font-semibold uppercase opacity-60' htmlFor='board-desc'>描述</label>
                    <textarea
                        id='board-desc'
                        rows={2}
                        className='w-full resize-none rounded border border-main-fg/20 bg-transparent px-2 py-1.5 outline-none focus:border-button-bg'
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                    />
                </div>

                <div>
                    <div className='mb-1 text-xs font-semibold uppercase opacity-60'>选择起点</div>
                    <div className='grid max-h-52 grid-cols-2 gap-2 overflow-auto'>
                        {templates.map((item) => (
                            <button
                                key={item.id}
                                type='button'
                                onClick={() => setTemplateId(item.id)}
                                className={classnames(
                                    'rounded border px-3 py-2 text-left text-sm',
                                    item.id === templateId ? 'border-button-bg bg-button-bg/10' : 'border-main-fg/15 hover:bg-main-fg/5',
                                )}
                            >
                                <span className='mr-2' aria-hidden={true}>{item.icon}</span>
                                {item.title}
                            </button>
                        ))}
                    </div>
                    {template && (
                        <div className='mt-2 flex items-center gap-2 text-xs opacity-60'>
                            <Icon name={viewIcons.board} size={13}/>
                            {template.cardProperties.length} 个属性 · 自动创建 4 个视图
                        </div>
                    )}
                </div>

                <div className='flex justify-end gap-2'>
                    <button type='button' className='Button Button--text' onClick={onClose}>取消</button>
                    <button type='submit' className='Button Button--primary'>创建</button>
                </div>
            </form>
        </Dialog>
    )
}
